import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Plus, Edit, Trash2, Eye, EyeOff, Save, X, ArrowLeft,
  Image, Video, FileText, Bold, Italic, 
  Underline, List, Link as LinkIcon, Heading1, Heading2, Quote, Code,
  Lock, ListOrdered, Settings
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Enhanced Rich Text Editor Component
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');

  // Sync local value with props
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onChange(newValue);
  };

  const insertAtCursor = (before, after = '') => {
    const textarea = document.getElementById('content-editor');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = localValue.substring(start, end);
    const newText = localValue.substring(0, start) + before + selectedText + after + localValue.substring(end);
    
    setLocalValue(newText);
    onChange(newText);
    
    // Restore cursor position
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = start + before.length;
      textarea.selectionEnd = start + before.length + selectedText.length;
    }, 0);
  };

  const addLink = () => {
    const url = window.prompt('Podaj URL linku:');
    if (url) {
      insertAtCursor(`<a href="${url}" target="_blank" class="text-gold underline">`, '</a>');
    }
  };

  const addImage = () => {
    const url = window.prompt('Podaj URL obrazka:');
    if (url) {
      insertAtCursor(`<img src="${url}" alt="" class="rounded-lg max-w-full my-4" />`);
    }
  };

  const addYoutube = () => {
    const url = window.prompt('Podaj URL filmu YouTube:');
    if (url) {
      const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1];
      if (videoId) {
        insertAtCursor(`<div class="my-4 aspect-video"><iframe width="100%" height="100%" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen class="rounded-lg"></iframe></div>`);
      } else {
        alert('Nieprawidłowy URL YouTube');
      }
    }
  };

  const toolbarButtons = [
    { icon: Bold, action: () => insertAtCursor('<strong>', '</strong>'), title: 'Pogrubienie' },
    { icon: Italic, action: () => insertAtCursor('<em>', '</em>'), title: 'Kursywa' },
    { icon: Underline, action: () => insertAtCursor('<u>', '</u>'), title: 'Podkreślenie' },
    { type: 'divider' },
    { icon: Heading1, action: () => insertAtCursor('<h2 class="text-2xl font-serif text-navy mt-6 mb-3">', '</h2>'), title: 'Nagłówek H2' },
    { icon: Heading2, action: () => insertAtCursor('<h3 class="text-xl font-serif text-navy mt-4 mb-2">', '</h3>'), title: 'Nagłówek H3' },
    { type: 'divider' },
    { icon: List, action: () => insertAtCursor('<ul class="list-disc pl-6 my-2"><li>', '</li></ul>'), title: 'Lista punktowa' },
    { icon: ListOrdered, action: () => insertAtCursor('<ol class="list-decimal pl-6 my-2"><li>', '</li></ol>'), title: 'Lista numerowana' },
    { icon: Quote, action: () => insertAtCursor('<blockquote class="border-l-4 border-gold pl-4 italic text-navy/60 my-4">', '</blockquote>'), title: 'Cytat' },
    { icon: Code, action: () => insertAtCursor('<pre class="bg-navy/5 rounded-lg p-4 overflow-x-auto my-4"><code>', '</code></pre>'), title: 'Blok kodu' },
    { type: 'divider' },
    { icon: LinkIcon, action: addLink, title: 'Dodaj link' },
    { icon: Image, action: addImage, title: 'Dodaj obrazek' },
    { icon: Video, action: addYoutube, title: 'Dodaj wideo YouTube' },
  ];

  return (
    <div className="border border-gold/20 rounded-xl overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 bg-white/50 backdrop-blur-sm border-b border-gold/20 flex-wrap">
        {toolbarButtons.map((btn, i) => (
          btn.type === 'divider' ? (
            <div key={i} className="w-px h-6 bg-navy/10 mx-1" />
          ) : (
            <button
              key={i}
              type="button"
              onClick={btn.action}
              title={btn.title}
              className="p-2 hover:bg-gold/10 rounded-lg transition-colors text-navy/60 hover:text-navy"
            >
              <btn.icon className="w-4 h-4" />
            </button>
          )
        ))}
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            showPreview ? 'bg-gold/20 text-gold' : 'hover:bg-navy/10 text-navy/60'
          }`}
        >
          {showPreview ? 'Edytuj' : 'Podgląd'}
        </button>
      </div>

      {/* Editor / Preview */}
      {showPreview ? (
        <div 
          className="min-h-[300px] p-5 bg-white/30 blog-content prose prose-navy max-w-none"
          dangerouslySetInnerHTML={{ __html: localValue || '<p class="text-navy/40">Brak treści...</p>' }}
        />
      ) : (
        <textarea
          id="content-editor"
          value={localValue}
          onChange={handleChange}
          placeholder={placeholder || 'Zacznij pisać... (możesz używać HTML)'}
          className="w-full min-h-[300px] p-5 bg-white/30 text-navy font-light resize-none focus:outline-none font-mono text-sm"
        />
      )}
    </div>
  );
};

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/admin/verify`, {}, {
        auth: {
          username: 'admin',
          password: password
        }
      });
      onLogin(password);
    } catch (err) {
      setError('Nieprawidłowe hasło');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 pt-32 pb-20 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md px-6"
      >
        <div className="liquid-glass-card p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-navy/10 flex items-center justify-center">
              <Lock className="w-8 h-8 text-navy" />
            </div>
            <h1 className="font-serif text-2xl text-navy mb-2">Panel administracyjny</h1>
            <p className="text-navy/60 text-sm">Wprowadź hasło, aby kontynuować</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Hasło"
                className="w-full px-4 py-3 liquid-glass-input text-navy text-center tracking-widest"
                autoFocus
              />
            </div>

            {error && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-red-500 text-sm text-center"
              >
                {error}
              </motion.p>
            )}

            <motion.button
              type="submit"
              disabled={loading || !password}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full liquid-glass-button py-3 disabled:opacity-50"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto" />
              ) : (
                'Zaloguj się'
              )}
            </motion.button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/blog"
              className="text-navy/50 hover:text-gold text-sm transition-colors"
            >
              ← Wróć do bloga
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const BlogAdmin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authPassword, setAuthPassword] = useState('');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    cover_image: '',
    author: 'Admin',
    category: 'Data Analytics',
    tags: [],
    published: false
  });

  const [newTag, setNewTag] = useState('');
  const [embedModal, setEmbedModal] = useState({ open: false, type: null });
  const [embedUrl, setEmbedUrl] = useState('');

  const axiosConfig = {
    auth: {
      username: 'admin',
      password: authPassword
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchPosts();
    }
  }, [isAuthenticated]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/admin/blog`, axiosConfig);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      if (error.response?.status === 401) {
        setIsAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (password) => {
    setAuthPassword(password);
    setIsAuthenticated(true);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      ...formData,
      title,
      slug: editingPost ? formData.slug : generateSlug(title)
    });
  };

  const handleAddTag = () => {
    if (newTag && !formData.tags.includes(newTag)) {
      setFormData({ ...formData, tags: [...formData.tags, newTag] });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove)
    });
  };

  const handleEmbed = () => {
    let embedCode = '';
    
    if (embedModal.type === 'youtube') {
      const videoId = embedUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1];
      if (videoId) {
        embedCode = `\n<div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:1.5rem 0;"><iframe src="https://www.youtube.com/embed/${videoId}" style="position:absolute;top:0;left:0;width:100%;height:100%;border-radius:12px;" frameborder="0" allowfullscreen></iframe></div>\n`;
      }
    } else if (embedModal.type === 'map') {
      embedCode = `\n<div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:1.5rem 0;"><iframe src="${embedUrl}" style="position:absolute;top:0;left:0;width:100%;height:100%;border-radius:12px;border:0;" allowfullscreen loading="lazy"></iframe></div>\n`;
    } else if (embedModal.type === 'image') {
      embedCode = `\n<img src="${embedUrl}" alt="" style="max-width:100%;border-radius:12px;margin:1.5rem 0;" />\n`;
    }

    if (embedCode) {
      setFormData({ ...formData, content: formData.content + embedCode });
    }
    
    setEmbedModal({ open: false, type: null });
    setEmbedUrl('');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingPost) {
        await axios.put(`${API}/admin/blog/${editingPost.id}`, formData, axiosConfig);
      } else {
        await axios.post(`${API}/admin/blog`, formData, axiosConfig);
      }
      await fetchPosts();
      resetForm();
    } catch (error) {
      console.error('Error saving post:', error);
      alert(error.response?.data?.detail || 'Błąd podczas zapisywania');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      cover_image: post.cover_image || '',
      author: post.author,
      category: post.category,
      tags: post.tags || [],
      published: post.published
    });
    setShowEditor(true);
  };

  const handleDelete = async (postId) => {
    if (window.confirm('Czy na pewno chcesz usunąć ten artykuł?')) {
      try {
        await axios.delete(`${API}/admin/blog/${postId}`, axiosConfig);
        await fetchPosts();
      } catch (error) {
        console.error('Error deleting post:', error);
      }
    }
  };

  const togglePublish = async (post) => {
    try {
      await axios.put(`${API}/admin/blog/${post.id}`, { published: !post.published }, axiosConfig);
      await fetchPosts();
    } catch (error) {
      console.error('Error toggling publish:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      cover_image: '',
      author: 'Admin',
      category: 'Data Analytics',
      tags: [],
      published: false
    });
    setEditingPost(null);
    setShowEditor(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pl-PL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-stone-100 pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-12"
        >
          <div>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-navy/60 hover:text-gold transition-colors duration-300 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              Wróć do bloga
            </Link>
            <h1 className="font-serif text-3xl md:text-4xl text-navy font-light">
              Panel administracyjny
            </h1>
          </div>
          
          <div className="flex items-center gap-3">
            {!showEditor && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowEditor(true)}
                className="liquid-glass-button px-6 py-3 flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Nowy artykuł
              </motion.button>
            )}
            <button
              onClick={() => setIsAuthenticated(false)}
              className="px-4 py-2 text-navy/50 hover:text-red-500 text-sm transition-colors"
            >
              Wyloguj
            </button>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {showEditor ? (
            <motion.div
              key="editor"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-6"
            >
              <div className="flex items-center justify-between">
                <h2 className="font-serif text-2xl text-navy">
                  {editingPost ? 'Edytuj artykuł' : 'Nowy artykuł'}
                </h2>
                <button
                  onClick={resetForm}
                  className="p-2 hover:bg-navy/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-navy/60" />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <div>
                    <label className="text-xs text-navy/50 font-light uppercase tracking-wider mb-2 block">
                      Tytuł
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={handleTitleChange}
                      placeholder="Tytuł artykułu"
                      className="w-full px-4 py-3 liquid-glass-input text-navy font-light"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-navy/50 font-light uppercase tracking-wider mb-2 block">
                      Slug (URL)
                    </label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      placeholder="url-artykulu"
                      className="w-full px-4 py-3 liquid-glass-input text-navy font-light"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-navy/50 font-light uppercase tracking-wider mb-2 block">
                      Krótki opis
                    </label>
                    <textarea
                      value={formData.excerpt}
                      onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                      placeholder="Krótki opis artykułu (widoczny na liście)"
                      rows={3}
                      className="w-full px-4 py-3 liquid-glass-input text-navy font-light resize-none"
                    />
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-navy/50 uppercase tracking-wider">Wstaw:</span>
                    <button
                      type="button"
                      onClick={() => setEmbedModal({ open: true, type: 'image' })}
                      className="flex items-center gap-1.5 px-3 py-1.5 liquid-glass text-navy/70 text-sm rounded-lg hover:bg-white/50"
                    >
                      <Image className="w-4 h-4" />
                      Zdjęcie
                    </button>
                    <button
                      type="button"
                      onClick={() => setEmbedModal({ open: true, type: 'youtube' })}
                      className="flex items-center gap-1.5 px-3 py-1.5 liquid-glass text-navy/70 text-sm rounded-lg hover:bg-white/50"
                    >
                      <Video className="w-4 h-4" />
                      YouTube
                    </button>
                    <button
                      type="button"
                      onClick={() => setEmbedModal({ open: true, type: 'map' })}
                      className="flex items-center gap-1.5 px-3 py-1.5 liquid-glass text-navy/70 text-sm rounded-lg hover:bg-white/50"
                    >
                      <Map className="w-4 h-4" />
                      Mapa
                    </button>
                  </div>

                  <div>
                    <label className="text-xs text-navy/50 font-light uppercase tracking-wider mb-2 block">
                      Treść (HTML)
                    </label>
                    <RichTextEditor
                      value={formData.content}
                      onChange={(content) => setFormData({ ...formData, content })}
                      placeholder="Napisz treść artykułu... (możesz używać HTML)"
                    />
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="liquid-glass-card p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Settings className="w-4 h-4 text-gold" />
                      <h3 className="font-medium text-navy">Ustawienia</h3>
                    </div>

                    <div className="mb-4">
                      <label className="text-xs text-navy/50 font-light uppercase tracking-wider mb-2 block">
                        Zdjęcie główne (URL)
                      </label>
                      <input
                        type="text"
                        value={formData.cover_image}
                        onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                        placeholder="https://..."
                        className="w-full px-3 py-2 liquid-glass-input text-navy text-sm"
                      />
                      {formData.cover_image && (
                        <img
                          src={formData.cover_image}
                          alt="Cover preview"
                          className="mt-2 w-full h-32 object-cover rounded-lg"
                        />
                      )}
                    </div>

                    <div className="mb-4">
                      <label className="text-xs text-navy/50 font-light uppercase tracking-wider mb-2 block">
                        Autor
                      </label>
                      <input
                        type="text"
                        value={formData.author}
                        onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                        className="w-full px-3 py-2 liquid-glass-input text-navy text-sm"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="text-xs text-navy/50 font-light uppercase tracking-wider mb-2 block">
                        Kategoria
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-3 py-2 liquid-glass-input text-navy text-sm"
                      >
                        <option>Data Analytics</option>
                        <option>Machine Learning</option>
                        <option>Business Intelligence</option>
                        <option>Cloud Computing</option>
                        <option>Industry News</option>
                      </select>
                    </div>

                    <div className="mb-4">
                      <label className="text-xs text-navy/50 font-light uppercase tracking-wider mb-2 block">
                        Tagi
                      </label>
                      <div className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                          placeholder="Dodaj tag"
                          className="flex-1 px-3 py-2 liquid-glass-input text-navy text-sm"
                        />
                        <button
                          type="button"
                          onClick={handleAddTag}
                          className="px-3 py-2 bg-gold/20 text-gold rounded-lg hover:bg-gold/30"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {formData.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-navy/10 text-navy/70 text-xs rounded-full"
                          >
                            #{tag}
                            <button
                              type="button"
                              onClick={() => handleRemoveTag(tag)}
                              className="hover:text-red-500"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm text-navy/70">Opublikowany</span>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, published: !formData.published })}
                        className={`w-12 h-6 rounded-full transition-colors duration-300 ${
                          formData.published ? 'bg-gold' : 'bg-navy/20'
                        }`}
                      >
                        <div
                          className={`w-5 h-5 bg-white rounded-full shadow transition-transform duration-300 ${
                            formData.published ? 'translate-x-6' : 'translate-x-0.5'
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="flex-1 px-4 py-3 liquid-glass text-navy/70 font-light rounded-xl hover:bg-white/50"
                    >
                      Anuluj
                    </button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleSave}
                      disabled={saving || !formData.title || !formData.content}
                      className="flex-1 liquid-glass-button px-4 py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {saving ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Zapisz
                        </>
                      )}
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="liquid-glass-card p-6 animate-pulse">
                      <div className="h-6 bg-navy/10 rounded w-1/2 mb-2"></div>
                      <div className="h-4 bg-navy/10 rounded w-1/3"></div>
                    </div>
                  ))}
                </div>
              ) : posts.length === 0 ? (
                <div className="text-center py-20">
                  <FileText className="w-16 h-16 text-navy/20 mx-auto mb-4" />
                  <p className="text-navy/50 text-lg mb-6">Brak artykułów</p>
                  <button
                    onClick={() => setShowEditor(true)}
                    className="liquid-glass-button px-6 py-3"
                  >
                    Utwórz pierwszy artykuł
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {posts.map((post, index) => (
                    <motion.div
                      key={post.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="liquid-glass-card p-6"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-serif text-xl text-navy">{post.title}</h3>
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full ${
                                post.published
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}
                            >
                              {post.published ? 'Opublikowany' : 'Szkic'}
                            </span>
                          </div>
                          <p className="text-navy/50 text-sm mb-2 line-clamp-2">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center gap-4 text-xs text-navy/40">
                            <span>{post.category}</span>
                            <span>{post.author}</span>
                            <span>{formatDate(post.created_at)}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => togglePublish(post)}
                            className="p-2 hover:bg-navy/10 rounded-lg transition-colors"
                            title={post.published ? 'Cofnij publikację' : 'Opublikuj'}
                          >
                            {post.published ? (
                              <EyeOff className="w-5 h-5 text-navy/50" />
                            ) : (
                              <Eye className="w-5 h-5 text-navy/50" />
                            )}
                          </button>
                          <button
                            onClick={() => handleEdit(post)}
                            className="p-2 hover:bg-navy/10 rounded-lg transition-colors"
                          >
                            <Edit className="w-5 h-5 text-navy/50" />
                          </button>
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-5 h-5 text-red-400" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Embed Modal */}
        <AnimatePresence>
          {embedModal.open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-navy/30 backdrop-blur-sm p-4"
              onClick={() => setEmbedModal({ open: false, type: null })}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="liquid-glass-card p-6 w-full max-w-md"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="font-serif text-xl text-navy mb-4">
                  {embedModal.type === 'youtube' && 'Wstaw film z YouTube'}
                  {embedModal.type === 'map' && 'Wstaw mapę Google'}
                  {embedModal.type === 'image' && 'Wstaw zdjęcie'}
                </h3>
                <input
                  type="text"
                  value={embedUrl}
                  onChange={(e) => setEmbedUrl(e.target.value)}
                  placeholder={
                    embedModal.type === 'youtube'
                      ? 'https://www.youtube.com/watch?v=...'
                      : embedModal.type === 'map'
                      ? 'URL z Google Maps (Embed)'
                      : 'https://... (URL zdjęcia)'
                  }
                  className="w-full px-4 py-3 liquid-glass-input text-navy mb-4"
                />
                <div className="flex gap-3">
                  <button
                    onClick={() => setEmbedModal({ open: false, type: null })}
                    className="flex-1 px-4 py-2 liquid-glass text-navy/70 rounded-xl"
                  >
                    Anuluj
                  </button>
                  <button
                    onClick={handleEmbed}
                    disabled={!embedUrl}
                    className="flex-1 liquid-glass-button px-4 py-2 disabled:opacity-50"
                  >
                    Wstaw
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BlogAdmin;
